import { API_URL } from "env";
import { mixLogOut } from '../services/mixin';

/* ================= TYPES ================= */
export interface ServiceCategoryItem {
    id: number;
    name: string;
    description: string | null;
    image: string | null;
    treatment_slip_id?: number | null;
    treatment_slip?: { id: number; name: string } | null;
    status: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

export interface TreatmentSlipItem {
    id: number;
    name: string;
    description?: string | null;
    avatar?: string | null;
    is_active?: number;
    created_at?: string;
    updated_at?: string;
}

export interface TreatmentSlipListResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: TreatmentSlipItem[];
    };
    msg: string;
}

export interface ServiceCategoryListResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: ServiceCategoryItem[];
    };
    msg: string;
}

export interface ServiceCategoryDetailResponse {
    status: boolean;
    data: ServiceCategoryItem;
    msg: string;
}

export interface ServiceItem {
    id: number;
    code: string;
    code_2: string;
    name: string;
    duration: number;
    category_id: number | null;
    category: ServiceCategoryItem | null;
    price: string;
    discount_price: string;
    priority: number;
    status: number;
    description: string | null;
    /** Khu vực / tính năng hiển thị dịch vụ (vd "checkin"); null là chưa gán */
    display_area?: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

export interface ServiceListResponse {
    status: boolean;
    data: {
        current_page: number;
        items: ServiceItem[];
        total: number;
    };
    msg: string;
}

export interface ServiceDetailResponse {
    status: boolean;
    data: ServiceItem;
    msg: string;
}

/* ================= SERVICE CATEGORY API ================= */

export async function getServiceCategories(params?: {
    search?: string;
    status?: number;
    take?: number;
    page?: number;
    sort?: string;
}): Promise<ServiceCategoryListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.search) query.append('search', params.search);
        if (params?.status !== undefined) query.append('status', String(params.status));
        if (params?.take) query.append('take', String(params.take));
        if (params?.page) query.append('page', String(params.page));
        if (params?.sort) query.append('sort', params.sort);
        const qs = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/service-categories${qs ? `?${qs}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        // Normalize: API returns data.data (array), map to data.items
        if (json.status && json.data && Array.isArray(json.data.data)) {
            json.data.items = json.data.data;
            delete json.data.data;
        }
        return json;
    } catch (error) {
        console.error('Failed to fetch service categories:', error);
        return { status: false, data: { total: 0, current_page: 1, items: [] }, msg: '' };
    }
}

export async function getServiceCategoryDetail(id: number): Promise<ServiceCategoryDetailResponse | null> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/service-categories/${id}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to fetch service category detail:', error);
        return null;
    }
}

export async function createServiceCategory(data: {
    name: string;
    description?: string;
    image?: string;
    treatment_slip_id?: number;
    status?: number;
}): Promise<{ status: boolean; data?: ServiceCategoryItem; msg: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/service-categories`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to create service category:', error);
        return { status: false, msg: 'Thêm danh mục thất bại' };
    }
}

export async function updateServiceCategory(id: number, data: {
    name?: string;
    description?: string;
    image?: string;
    treatment_slip_id?: number;
    status?: number;
}): Promise<{ status: boolean; data?: ServiceCategoryItem; msg: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/service-categories/${id}`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to update service category:', error);
        return { status: false, msg: 'Cập nhật danh mục thất bại' };
    }
}

export async function deleteServiceCategory(id: number): Promise<{ status: boolean; msg: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/service-categories/${id}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to delete service category:', error);
        return { status: false, msg: 'Xóa danh mục thất bại' };
    }
}

export async function getTreatmentSlips(params?: {
    search?: string;
    is_active?: number;
    take?: number;
    page?: number;
}): Promise<TreatmentSlipListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.search) query.append('search', params.search);
        if (params?.is_active !== undefined) query.append('is_active', String(params.is_active));
        if (params?.take) query.append('take', String(params.take));
        if (params?.page) query.append('page', String(params.page));
        const qs = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/treatment-slips${qs ? `?${qs}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        if (json.status && json.data && Array.isArray(json.data.data)) {
            json.data.items = json.data.data;
            delete json.data.data;
        }
        return json;
    } catch (error) {
        console.error('Failed to fetch treatment slips:', error);
        return { status: false, data: { total: 0, current_page: 1, items: [] }, msg: '' };
    }
}

/* ================= SERVICE API ================= */

export async function getServices(params?: {
    search?: string;
    status?: number;
    category_id?: number;
    /** Lọc theo khu vực hiển thị (vd "checkin"); "none" là dịch vụ chưa gán khu vực nào */
    display_area?: string;
    take?: number;
    page?: number;
    sort?: string;
}): Promise<ServiceListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.search) query.append('search', params.search);
        if (params?.status !== undefined) query.append('status', String(params.status));
        if (params?.category_id !== undefined) query.append('category_id', String(params.category_id));
        if (params?.display_area) query.append('display_area', params.display_area);
        if (params?.take) query.append('take', String(params.take));
        if (params?.page) query.append('page', String(params.page));
        if (params?.sort) query.append('sort', params.sort);
        const qs = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/services${qs ? `?${qs}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        // Normalize: API returns data.data (array), map to data.items
        if (json.status && json.data && Array.isArray(json.data.data)) {
            json.data.items = json.data.data;
            delete json.data.data;
        }
        return json;
    } catch (error) {
        console.error('Failed to fetch services:', error);
        return { status: false, data: { current_page: 1, items: [], total: 0 }, msg: '' };
    }
}

export async function getServiceDetail(id: number): Promise<ServiceDetailResponse | null> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/services/${id}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to fetch service detail:', error);
        return null;
    }
}

export async function createService(data: {
    code: string;
    code_2: string;
    name: string;
    duration?: number;
    category_id?: number;
    price: number;
    discount_price?: number;
    priority?: number;
    status: number;
    description?: string;
}): Promise<{ status: boolean; data?: ServiceItem; msg: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/services`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to create service:', error);
        return { status: false, msg: 'Thêm dịch vụ thất bại' };
    }
}

export async function updateService(id: number, data: {
    code?: string;
    code_2?: string;
    name?: string;
    duration?: number;
    category_id?: number;
    price?: number;
    discount_price?: number;
    priority?: number;
    status?: number;
    description?: string;
}): Promise<{ status: boolean; data?: ServiceItem; msg: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/services/${id}`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to update service:', error);
        return { status: false, msg: 'Cập nhật dịch vụ thất bại' };
    }
}

/**
 * Gán (`add`) hoặc gỡ (`remove`, về null) khu vực hiển thị cho nhiều dịch vụ cùng lúc.
 * Hiện chỉ dùng khu vực "checkin": dịch vụ hiển thị ở màn đăng ký của kiosk Check-in.
 */
export async function updateServicesDisplayArea(
    serviceIds: number[],
    action: 'add' | 'remove',
    displayArea = 'checkin',
): Promise<{ status: boolean; msg: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/services/update-display-area`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ display_area: displayArea, service_ids: serviceIds, action }),
        });

        if (response.status === 401) { mixLogOut(); }
        // Lỗi validation (422) vẫn có body JSON: lấy thông báo lỗi đầu tiên để hiển thị
        const json = await response.json().catch(() => null);
        if (!response.ok || !json?.status) {
            const firstError = json?.error && typeof json.error === 'object' ? Object.values(json.error)[0] : undefined;
            return { status: false, msg: String(firstError || json?.msg || '') };
        }
        return json;
    } catch (error) {
        console.error('Failed to update services display area:', error);
        return { status: false, msg: '' };
    }
}

export async function deleteService(id: number): Promise<{ status: boolean; msg: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/services/${id}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to delete service:', error);
        return { status: false, msg: 'Xóa dịch vụ thất bại' };
    }
}
